import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {Tooltip} from '@/components/tooltips';
import {Icon} from '@/components/icons/Icon';
import Spinner from '@/components/ui/Spinner';

import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import {navigate} from '@reach/router';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {buildCustomerDashboardURL} from '@/utils/urlBuilder';
import usePaginatedData from '@/utils/usePaginatedData';
import {FormattedDateTime} from '@/components/utils/FormattedDateTime';
import CustomerSettingsModal from './CustomerSettingsModal';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import useData from '@/utils/useData';
import {ROUTES} from '@/routing/route';
import TableComponent from '@/components/table/Table';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {useDConstants} from '@/hooks/useConfig';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {isModuleEnabled} from '@/utils/modules';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';
interface Customer {
    id: number;
    fullName: string;
    externalId: string;
    updatedOn: string;
    createdOn: string;
    optIn: boolean;
    optInDate?: string;
}

interface Story {
    id: number;
    assignedUrl: string;
    firstStoryboardHashid: string;
    firstStoryboardSlug: string;
}

interface UserData {
    permissions?: string[];
    currentModules?: {'addon-coop'?: boolean};
}

interface DConstants {
    datetimeFormat: string;
}

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const CustomerListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {dconstants} = useDConstants();
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const canChangeCustomer = hasPermission('fs.customer.update', userData?.permissions, true);
    const canCreateCustomer = hasPermission('fs.customer.create', userData?.permissions, true);

    const [personsStories] = useData<Story[]>(window.URLS.storiesResource, {
        storeResolver: 'personsStories',
        initialValue: [],
        requestParams: {
            params: {storyListType: 'personsStories'},
        },
    });

    const paginatedData = usePaginatedData(window.URLS.customersListResource, {
        storeResolver: 'customers',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    }) as {
        data: Customer[];
        loading: boolean;
        currentPage: number | null;
        setCurrentPage: (page: number) => void;
        pages: number[] | null;
    };


    const {
        data: customers,
        loading: loadingCustomers,
        currentPage,
        setCurrentPage,
        pages,
    } = paginatedData;

    const userCanAccessSpecialStoryViewObject = hasPermission('fs.story.list', userData?.permissions, true) || false;
    const userCanAccessCustomerResource = hasPermission('fs.customer.list', userData?.permissions, true) || false;

    if (!userCanAccessCustomerResource && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_this_view.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (customerId: number): void => {
        setSelectedCustomerId(customerId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedCustomerId(0);
    };


    let url = '#';
    let storyType = 'template';
    personsStories.forEach((story: Story) => {
        if (story.assignedUrl === 'customers') {
            if (story.id > 0) {
                url = ROUTES.storyboardViewRoute.replace(
                    ':activeStoryboardHashid', story.firstStoryboardHashid,
                ).replace(
                    ':activeStoryboardUrl', story.firstStoryboardSlug,
                );
                storyType = 'special';
            } else {
                url = ROUTES.storyboardTemplateViewRoute.replace(
                    ':activeStoryUrl', story.assignedUrl,
                ).replace(
                    ':activeStoryboardUrl', story.firstStoryboardSlug,
                );
            }
        }
    });

    const [datePart] = (configLoaded ? dconstants.datetimeFormat : 'yyyy-LL-dd HH:mm:ss').split(' ') || [''];

    if (loadingUserData) {
        return (
            <Spinner
                size="md"
                text={i18n.t('jt_loading_data_general.text')}
                centered
                fullHeight
            />
        );
    }

    const detailUrlGenerator = (rowData: Customer): string => {
        return buildCustomerDashboardURL(rowData.id);
    };

    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const customerPiiRestricted = restrictedFieldKeys.includes('customer_pii');

    const columns = [
        {
            header: i18n.t('th_name.text'),
            accessorKey: 'fullName',
            cell: (cell: any) => {
                if (customerPiiRestricted) {
                    return <RestrictedFieldIndicator />;
                }
                return <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />;
            },
        },
        {
            header: i18n.t('th_external.text'),
            accessorKey: 'externalId',
            cell: (cell: any) => {
                if (customerPiiRestricted) {
                    return <RestrictedFieldIndicator />;
                }
                return cell.getValue() || '';
            },
        },
        {
            id: 'i18ntthlastmodifiedtext',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: Customer) => {
                if (!cell.updatedOn || cell.updatedOn === '-') {
                    return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(cell.updatedOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_opt.text'),
            accessorKey: 'optIn',
            cell: (cell: any) => {
                if (!isModuleEnabled(userData, 'addon-coop')) {
                    return (
                        <Tooltip
                            content={i18n.t('tt_the_following.text')}
                            placement="top"
                        >
                            <div style={{
                                display: 'inline-block',
                            }}
                            >
                                <Icon name="mdi-alert-circle" size="sm" className="text-danger" />
                            </div>
                        </Tooltip>
                    );
                }

                if (cell.getValue() === true) {
                    if (cell.row.original.optInDate) {
                        return (
                            <>
                                <Icon name="mdi-check" size="sm" className="text-success" />
                                <span className="ml-1">
                                    (
                                    <FormattedDateTime
                                        dateTime={cell.row.original.optInDate}
                                        showTime={true}
                                        options={{setZone: false}}
                                    />
                                    )
                                </span>
                            </>
                        );
                    }
                    return (<Icon name="mdi-check" className="text-success" />);
                } else {
                    return (<Icon name="mdi-close" className="text-danger" />);
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <div className="d-flex align-items-center justify-content-end w-100">
                    <div className="d-inline-block mr-2">
                        {
                            userCanAccessSpecialStoryViewObject ? (
                                <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-eye"
                                        onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{
                                        display: 'inline-block',
                                        cursor: 'not-allowed',
                                    }}
                                    >
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            icon="mdi-eye"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="customer-list-eye-outlinesecondary-btn" />
                                    </div>
                                </Tooltip>
                            )
                        }
                    </div>
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                id="settings-dropdown-1"
                                data-cy="customer-list-outlineinfo-btn"
                            />

                            <Dropdown.Menu align="end">
                                {
                                    canChangeCustomer ? (
                                        <Dropdown.Item
                                            onClick={() => openSettingsModal(cell.row.original.id)}
                                        >
                                            <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="customer-list-btn">
                                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_this_view.text')} />
            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    {
                        canCreateCustomer ? (
                            <Tooltip content={i18n.t('jt_add.text')} placement="top">
                                <Button
                                    variant="info"
                                    size="sm"
                                    icon="mdi-plus"
                                    onClick={() => openSettingsModal(0)}
                                    data-cy="new-customer-btn"
                                    className="mb-2"
                                >
                                    {i18n.t('jt_add_new_customer.text')}
                                </Button>
                            </Tooltip>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'inline-block'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon="mdi-plus"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="customer-list-plus-btn"
                                        className="mb-2"
                                    >
                                        {i18n.t('jt_add_new_customer.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </Col>
            </Row>

            <LoadingWrapper
                loading={loadingCustomers}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="400px"
            >
                <div className="card">
                    <div className="card-body">
                        <TableComponent data={customers} columns={columns} />

                        <PaginationComponent
                            pages={pages}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            hideWhenSinglePage={false}
                        />
                    </div>
                </div>

                <CustomerSettingsModal
                    modalOpen={settingsModalOpen}
                    closeModal={closeSettingsModal}
                    activeCustomerId={selectedCustomerId}
                />
            </LoadingWrapper>
        </>
    );
});

export default CustomerListView; 